import type { BidiInstance } from 'bidi-js';
import type { ParsedSegment, TextSegmentParser } from './types';
import type { TextEffectsSharedMapperProps } from '../../TextEffectsShared.types';
import { isVisibleSegment } from './isVisibleSegment';

/**
  Character-level reorder algorithm explanation:
  Let's have the user input string "Hello שלום" (original string).
  "bidi-js" - is responsible for handling bidirectional text in LTR and RTL contexts.
  Operation level: character-level (position in the stored source string).
  "bidi-js" helps us to find the flip ranges in the ORIGINAL string.
  We need to flip, for example, the range between "ש" and "ם".
  In this case, the flip range indexes are [6, 9] (6 refers to "ש" and 9 refers to "ם").
  "Intl.Segmenter" - is responsible for CORRECT splitting the string into segments.
  Operation level: visual graphemes (what user sees in the browser).
  "Intl.Segmenter" takes into consideration (aka why not `str.split()`):
    - CJK (Chinese, Japanese, Korean) characters
    - Emojis (including complex ones like 👨‍👩‍👧‍👦)
    - Ligatures (e.g., "æ", "œ")
    - Accented characters and diacritics (e.g., "café", "naïve")
    - Letters from any language
    - Correct split by words where `.split()` fails (sentenses without spaces between words)
    - Mixed langs in 1 sentence (also with mixed directions)
*/

export const parseGrapheme: TextSegmentParser = (
  str: string,
  textDirection: TextEffectsSharedMapperProps['textDirection'],
  segmenter: Intl.Segmenter,
  normalize: (segments: Array<ParsedSegment>) => Array<Array<ParsedSegment>>,
  bidi: BidiInstance,
) => {
  let preparedString = str;

  try {
    const embeddingLevels = bidi.getEmbeddingLevels(str, textDirection);
    preparedString = bidi.getReorderedString(str, embeddingLevels);
  } catch (error) {}

  const segments = Array.from(segmenter.segment(preparedString), segment => ({
    data: segment.segment,
    isVisible: isVisibleSegment(segment.segment),
  }));

  return {
    segmentsBrokenByLines: normalize(segments),
  };
};
